/*
 * Copyright 2025 Intuition Robotics ltd
 */
import * as React from "react";
import {ChangeEvent, createContext, useEffect, useRef, useState} from "react";
import '../../css/style.css';
import HomeScreen from "@components/page/HomeScreen";
import ShowSelected from "@components/page/ShowSelected";
import SentScreen from "@components/page/SentScreen";
import ImageCropperScreen from "@components/ImageCropperScreen";
import canvasSize from 'canvas-size';
import {t} from "@util/i18n";

type Props = {
    selectedImages: File[],
    onImagesSent: () => void,
    onImagesDeleted: (idx: number) => void,
    openFileInput: () => void
    imagesSent: boolean
    setImagesSent: (sent: boolean) => void,
    cropImage: string | null
    handleImageCropperClose: () => void
    addCroppedImage: (image: File) => void;
    imageToBig: boolean;
    setImageToBig: (status: boolean) => void
};

function AppHomeImpl({
                         selectedImages,
                         onImagesSent,
                         onImagesDeleted,
                         openFileInput,
                         imagesSent,
                         setImagesSent,
                         cropImage,
                         handleImageCropperClose,
                         addCroppedImage,
                         imageToBig,
                         setImageToBig
                     }: Props) {
    if (cropImage)
        return <ImageCropperScreen originalImage={cropImage} onClose={handleImageCropperClose} addCroppedImage={addCroppedImage}/>;

    if (imagesSent)
        return <SentScreen openFileFunc={() => {
            onImagesSent();
            openFileInput();
        }} selectedImages={selectedImages}/>;

    if (selectedImages.length !== 0)
        return <ShowSelected
            selectedImages={selectedImages}
            openFileFunc={openFileInput}
            setSent={() => {
                setImagesSent(true);
            }}
            deleteFunc={onImagesDeleted}
            imageToBig={imageToBig}
            setImageToBig={setImageToBig}
        />;

    return <HomeScreen openFileFunc={openFileInput} imageToBig={imageToBig} setImageToBig={setImageToBig}/>;
}

type ContextType = {
    setAlertOn: (status: boolean) => void
}

export const SetAlertOnContext = createContext<ContextType>({
    setAlertOn: () => {
    }
});

function AppHome() {
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const [selectedImages, setSelectedImages] = useState<File[]>([]);
    const [imagesSent, setImagesSent] = useState(false);
    const [cropImage, setCropImage] = useState<string | null>(null);
    const [canvasMaxSize, setCanvasMaxSize] = useState({width: 0, height: 0});
    const [imageToBig, setImageToBig] = useState(false);
    const [alertOn, setAlertOn] = useState(false);


    useEffect(() => {
        const checkCanvasSize = async () => {
            const results = await canvasSize.maxArea({
                max: 18000,
                min: 1,
                step: 100,
                usePromise: true,
                onError(_results: any) {
                }
            });

            if (results) {
                console.log('🟢 max canvasSize: ', results);
                setCanvasMaxSize({width: results.width, height: results.height});
            } else {
                alert(t("browserNotSupported"));
            }
        };
        checkCanvasSize().catch(console.error);
    }, []);

    const handleImageCropperClose = () => {
        setCropImage(null);
    };

    const addCroppedImage = (croppedImage: File) => {


        setSelectedImages([croppedImage,
            ...selectedImages]);
    };


    const handleImageSelection = (event: ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        const fileInput = event.target

        if (file) {
            const fileSizeInMB = file.size / (1024 * 1024);
            if (fileSizeInMB > 10) {
                setImageToBig(true);
                return;

            }


            const reader = new FileReader();
            reader.onload = (readerEvent) => {
                const img = new Image();
                img.src = readerEvent.target?.result as string;

                img.onload = () => {
                    const maxWidth = canvasMaxSize.width;
                    const maxHeight = canvasMaxSize.height;
                    const canvas = document.createElement('canvas');
                    const ctx = canvas.getContext('2d');

                    if (maxWidth === 0 || maxHeight === 0) {
                        return;
                    }

                    let newWidth = img.width;
                    let newHeight = img.height;

                    if (img.width > maxWidth) {
                        newWidth = maxWidth;
                        newHeight = (maxWidth / img.width) * img.height;
                    }

                    if (newHeight > maxHeight) {
                        newHeight = maxHeight;
                        newWidth = (maxHeight / img.height) * img.width;
                    }

                    canvas.width = newWidth;
                    canvas.height = newHeight;

                    ctx?.drawImage(img, 0, 0, newWidth, newHeight);

                    const resizedDataURL = canvas.toDataURL('image/jpeg');
                    setCropImage(resizedDataURL);
                    setImagesSent(false);
                };
            };

            reader.readAsDataURL(file);
            fileInput.value = ''
        }
    };


    const handleDeleteConfirm = (delIdx: number) => {
        setSelectedImages((prev) => prev.filter((_, index) => index !== delIdx));
    };

    return (
        <SetAlertOnContext.Provider value={{setAlertOn}}>
            <div className={`appWrapper ${alertOn ? 'alertScreen' : ''}`}>
                <>
                    <AppHomeImpl
                        selectedImages={selectedImages}
                        onImagesSent={() => {
                            setSelectedImages([]);
                        }}
                        onImagesDeleted={handleDeleteConfirm}
                        openFileInput={() => fileInputRef.current?.click()}
                        imagesSent={imagesSent}
                        setImagesSent={setImagesSent}
                        cropImage={cropImage}
                        handleImageCropperClose={handleImageCropperClose}
                        addCroppedImage={addCroppedImage}
                        imageToBig={imageToBig}
                        setImageToBig={setImageToBig}
                    />

                    <input
                        type="file"
                        accept="image/*, image/heic"
                        onChange={handleImageSelection}
                        id="image-upload"
                        style={{display: 'none'}}
                        ref={fileInputRef}
                    />
                </>
            </div>
        </SetAlertOnContext.Provider>
    );
}

export default AppHome;